import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  Link,
  NativeSelect,
  Spinner,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  ArrowLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExternalLinkIcon,
  RefreshCwIcon,
  ShieldAlertIcon,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { PageHeader } from '~/components/shell'
import { useAuth } from '~/contexts/AuthContext'
import { activityApi, type ExternalLinkActivityCount } from '~/lib/api'

export const Route = createFileRoute('/_app/admin/activity-external-links')({
  component: ActivityExternalLinksPage,
})

const PAGE_SIZE = 20

function formatUrl(url: string) {
  try {
    const parsed = new URL(url)
    return parsed.hostname + (parsed.pathname !== '/' ? parsed.pathname : '')
  } catch {
    return url
  }
}

function ActivityExternalLinksPage() {
  const { hasMinRole, isLoading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [currentPage, setCurrentPage] = useState(1)
  const [statsDays, setStatsDays] = useState(7)

  const isAdmin = hasMinRole('admin')

  // Redirect non-admins after a short delay
  useEffect(() => {
    if (!authLoading && !isAdmin) {
      const timer = setTimeout(() => {
        navigate({ to: '/' })
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [authLoading, isAdmin, navigate])

  // Show access denied for non-admins
  if (!authLoading && !isAdmin) {
    return (
      <Flex h="100%" align="center" justify="center">
        <VStack gap={4} textAlign="center" p={8}>
          <Box p={4} borderRadius="full" bg="red.100" _dark={{ bg: 'red.900' }}>
            <ShieldAlertIcon size={48} color="var(--chakra-colors-red-500)" />
          </Box>
          <Heading size="lg" color="red.500">
            Access Denied
          </Heading>
          <Text color="content.secondary" maxW="400px">
            This page is restricted to <strong>admin</strong> users only.
          </Text>
          <Text fontSize="sm" color="content.secondary">
            Redirecting to home page...
          </Text>
        </VStack>
      </Flex>
    )
  }

  // Fetch external links data
  const {
    data: linksData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['activity-external-links', statsDays, currentPage],
    queryFn: () =>
      activityApi.externalLinks({ days: statsDays, page: currentPage, pageSize: PAGE_SIZE }),
    retry: false,
  })

  const links = linksData?.links || []
  const totalLinks = linksData?.total || 0
  const totalPaginationPages = Math.ceil(totalLinks / PAGE_SIZE)

  return (
    <Box maxW="1400px" mx="auto">
      <Button variant="ghost" size="sm" mb={2} onClick={() => navigate({ to: '/admin/activity' })}>
        <ArrowLeftIcon size={16} />
        Back to Activity
      </Button>
      <PageHeader
        title="External Link Clicks"
        subtitle="All external links ordered by clicks"
        actions={
          <>
            <NativeSelect.Root size="sm" w="160px">
              <NativeSelect.Field
                value={statsDays}
                onChange={(e) => {
                  setStatsDays(Number(e.target.value))
                  setCurrentPage(1)
                }}
              >
                <option value={1}>Last 24 hours</option>
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
              </NativeSelect.Field>
            </NativeSelect.Root>
            <Button size="sm" onClick={() => refetch()}>
              <RefreshCwIcon size={16} />
              Refresh
            </Button>
          </>
        }
      />

      <VStack align="stretch" gap={6}>
        {/* External Links Table */}
        <Card.Root>
          <Card.Header>
            <Flex justify="space-between" align="center">
              <Heading size="sm">External Links by Clicks</Heading>
              <Text fontSize="sm" color="content.secondary">
                {totalLinks} total links
              </Text>
            </Flex>
          </Card.Header>
          <Table.Root>
            <Table.Header>
              <Table.Row bg="bg.subtle">
                <Table.ColumnHeader w="60px">Rank</Table.ColumnHeader>
                <Table.ColumnHeader>Link</Table.ColumnHeader>
                <Table.ColumnHeader w="150px" textAlign="right">
                  Clicks
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {isLoading ? (
                <Table.Row>
                  <Table.Cell colSpan={3}>
                    <Flex justify="center" py={8}>
                      <Spinner />
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              ) : links.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={3}>
                    <Text textAlign="center" py={8} color="content.secondary">
                      No external link clicks found
                    </Text>
                  </Table.Cell>
                </Table.Row>
              ) : (
                links.map((link: ExternalLinkActivityCount, index: number) => {
                  const rank = (currentPage - 1) * PAGE_SIZE + index + 1
                  return (
                    <Table.Row key={`${link.linkName}-${link.externalUrl}`}>
                      <Table.Cell>
                        <Badge
                          colorPalette={
                            rank === 1
                              ? 'yellow'
                              : rank === 2
                                ? 'gray'
                                : rank === 3
                                  ? 'orange'
                                  : 'blue'
                          }
                          variant="subtle"
                          px={2}
                        >
                          #{rank}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell>
                        <VStack align="flex-start" gap={0}>
                          <Text fontSize="sm" fontWeight="medium">
                            {link.linkName}
                          </Text>
                          <Link
                            href={link.externalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            fontSize="xs"
                            color="blue.500"
                            _hover={{ textDecoration: 'underline' }}
                          >
                            <HStack gap={1}>
                              <span>{formatUrl(link.externalUrl)}</span>
                              <ExternalLinkIcon size={10} />
                            </HStack>
                          </Link>
                        </VStack>
                      </Table.Cell>
                      <Table.Cell textAlign="right">
                        <Badge colorPalette="purple" variant="subtle" fontSize="sm">
                          {link.clicks} clicks
                        </Badge>
                      </Table.Cell>
                    </Table.Row>
                  )
                })
              )}
            </Table.Body>
          </Table.Root>
        </Card.Root>

        {/* Pagination */}
        {totalPaginationPages > 1 && (
          <Flex justify="space-between" align="center">
            <Text fontSize="sm" color="content.secondary">
              Showing {(currentPage - 1) * PAGE_SIZE + 1}-
              {Math.min(currentPage * PAGE_SIZE, totalLinks)} of {totalLinks} links
            </Text>
            <HStack gap={2}>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeftIcon size={16} />
                Previous
              </Button>
              <HStack gap={1}>
                {Array.from({ length: Math.min(totalPaginationPages, 5) }, (_, i) => {
                  let pageNum: number
                  if (totalPaginationPages <= 5) {
                    pageNum = i + 1
                  } else if (currentPage <= 3) {
                    pageNum = i + 1
                  } else if (currentPage >= totalPaginationPages - 2) {
                    pageNum = totalPaginationPages - 4 + i
                  } else {
                    pageNum = currentPage - 2 + i
                  }
                  return (
                    <Button
                      key={pageNum}
                      size="sm"
                      variant={pageNum === currentPage ? 'solid' : 'outline'}
                      colorPalette={pageNum === currentPage ? 'blue' : undefined}
                      onClick={() => setCurrentPage(pageNum)}
                      minW="40px"
                    >
                      {pageNum}
                    </Button>
                  )
                })}
              </HStack>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCurrentPage((p) => Math.min(totalPaginationPages, p + 1))}
                disabled={currentPage === totalPaginationPages}
              >
                Next
                <ChevronRightIcon size={16} />
              </Button>
            </HStack>
          </Flex>
        )}
      </VStack>
    </Box>
  )
}
